import { PRODUCT_IDS } from '@/src/constants/products';
import { ProductId } from '@/src/types/Products';
import { useAccountContext, useCustomerContext } from '@ovotech/ui-tools';
import {
  BoltonPlan,
  BoltonPlanStatus,
  BoltonProductType,
  KaluzaDevice,
  KaluzaVehicleDevice,
  GetSignupResponse,
  boltons,
} from '@monovo/ev-anytime';
import { usePlanDetailsQuery } from './usePlanDetailsQuery';
import { useSignupQuery } from './useSignupQuery';

// Type for bolton data extracted from BoltonPlan
// Uses ProductId and string dates to maintain compatibility with existing consumers
export type ExtractedBolton = {
  productId: ProductId;
  status: string;
  id?: number;
  name?: string;
  activeFrom?: string;
  activeUntil?: string;
};

export type ChargeAnytimeBoltonState = {
  isFetching: boolean;
  isLoading: boolean;
  boltonPlan?: BoltonPlan | null;
  payAsYouDriveBolton?: ExtractedBolton;
  payAsYouDriveBoltonActive: boolean;
  hasChargeAnytime: boolean;
  payAsYouDriveMonthlyBolton?: ExtractedBolton;
  hasActivePayMonthlyBolton: boolean;
  hasInitializedPayMonthlyBolton: boolean;
  payAsYouDriveBoltonInitialised: boolean;
  chargeAnytimeBolton?: ExtractedBolton;
  signupIntegrationType?: GetSignupResponse['integrationType'];
  vehicles?: KaluzaVehicleDevice[];
  chargers?: KaluzaDevice[];
  eligibleSwitchProducts?: BoltonProductType[];
  refetchPlanDetails: () => void;
  refetchSignup: () => void;
};

/**
 * Extracts an array of bolton-like objects from BoltonPlan structure.
 * This allows reuse of existing helper functions (findPaydBolton, findMonthlyBolton).
 */
const extractBoltonsFromPlan = (
  boltonPlan: BoltonPlan | undefined,
): ExtractedBolton[] => {
  if (!boltonPlan || boltonPlan.status === BoltonPlanStatus.NoChargeAnytime) {
    return [];
  }

  const extractedBoltons: ExtractedBolton[] = [];

  if (boltonPlan.current) {
    extractedBoltons.push({
      productId: boltonPlan.current.productId as ProductId,
      status: boltonPlan.current.status,
      id: boltonPlan.current.id,
      name: boltonPlan.current.name,
      // API returns dates as strings, cast to match BoltonInstance type
      activeFrom: String(boltonPlan.current.activeFrom),
      ...('activeUntil' in boltonPlan.current && {
        activeUntil: String(boltonPlan.current.activeUntil),
      }),
    });
  }

  if (boltonPlan.future) {
    extractedBoltons.push({
      productId: boltonPlan.future.productId as ProductId,
      status: boltonPlan.future.status,
      id: boltonPlan.future.id,
      name: boltonPlan.future.name,
      activeFrom: boltonPlan.future.activeFrom
        ? String(boltonPlan.future.activeFrom)
        : undefined,
    });
  }

  return extractedBoltons;
};

export const payAsYouDriveProducts = [PRODUCT_IDS.payasyoudrive];

export const payMonthlyProducts = [
  PRODUCT_IDS.paymTierOne,
  PRODUCT_IDS.paymTierTwo,
  PRODUCT_IDS.paymMultiTierOne,
  PRODUCT_IDS.paymMultiTierTwo,
  PRODUCT_IDS.paymOemTierOne,
];

export const chargeAnytimeProducts = [
  ...payAsYouDriveProducts,
  ...payMonthlyProducts,
];

export const isChargeAnytimeProduct = (id: ProductId) =>
  chargeAnytimeProducts.includes(id);

export const isChargeAnytimePayMonthlyProducts = (id: ProductId) =>
  payMonthlyProducts.includes(id);

/**
 * Hook for managing Charge Anytime bolton state.
 * Uses planDetailsQuery as the single source of truth for bolton data.
 * The /plan-details endpoint internally calls /products on the backend,
 * so we no longer need a separate products query (ZPBB-5419).
 */

export const useChargeAnytimeBoltonState = (): ChargeAnytimeBoltonState => {
  const { accountId } = useAccountContext();
  const { customerId } = useCustomerContext();

  // planDetailsQuery is now the single source of truth for bolton data
  // The /plan-details endpoint internally calls /products on the backend
  const planDetailsQuery = usePlanDetailsQuery(true, accountId!);

  const planDetailsData = planDetailsQuery.data;

  // Extract boltons array from BoltonPlan structure for use with helper functions
  const boltonsArray = extractBoltonsFromPlan(planDetailsData?.boltons);

  // Downstream queries depend on boltons existing to avoid unnecessary API calls
  const hasBoltons = boltonsArray.length > 0;

  // Signup query is automatically enabled when boltons exist
  const signupQuery = useSignupQuery(hasBoltons, accountId!, customerId!);

  const signupIntegrationType = signupQuery.data?.integrationType;

  const payAsYouDriveBolton = boltons.findPaydBolton(boltonsArray);
  const payAsYouDriveMonthlyBolton = boltons.findMonthlyBolton(boltonsArray);

  // Extract statuses to reduce repeated optional chaining
  const paydStatus = payAsYouDriveBolton?.status;
  const paymStatus = payAsYouDriveMonthlyBolton?.status;

  const payAsYouDriveBoltonActive = paydStatus === 'Active';
  const payAsYouDriveBoltonInitialised = paydStatus === 'Initialized';

  const hasActivePayMonthlyBolton = paymStatus === 'Active';
  const hasInitializedPayMonthlyBolton = paymStatus === 'Initialized';

  const chargeAnytimeBolton = payAsYouDriveBolton || payAsYouDriveMonthlyBolton;

  const hasChargeAnytime = Boolean(chargeAnytimeBolton);

  const isLoading = signupQuery.isLoading || planDetailsQuery.isLoading;

  const isFetching = signupQuery.isFetching || planDetailsQuery.isFetching;

  return {
    isLoading,
    isFetching,
    payAsYouDriveBolton,
    payAsYouDriveBoltonActive,
    hasChargeAnytime,
    payAsYouDriveBoltonInitialised,
    chargeAnytimeBolton,
    refetchPlanDetails: planDetailsQuery.refetch,
    refetchSignup: signupQuery.refetch,

    payAsYouDriveMonthlyBolton,
    hasActivePayMonthlyBolton,
    hasInitializedPayMonthlyBolton,
    ...(signupIntegrationType && { signupIntegrationType }),

    ...(planDetailsData && {
      boltonPlan: planDetailsData.boltons,
      vehicles: planDetailsData.vehicles,
      chargers: planDetailsData.chargers,
      eligibleSwitchProducts: planDetailsData.eligibleSwitchProducts,
    }),
  };
};
